import React, { useState, useEffect, useMemo } from "react";

import { Card, Tooltip } from "reactstrap";
import { Link } from "react-router-dom";
import NavCardBody from "./NavCardBody";
import { useVsiAppContext } from "../stores/VsiApp";
import { VsiH6, VsiSmallText, VsiSpanText } from "../styled/App.styled";
import { constructQsp, snakeCaseToTitleCase } from "../utils/Helpers";
import themeStyles from "../utils/themeStyles";
import { DeleteAuth } from "../utils/Verbs";
import EnterpriseSummary from "../bookmark_visuals/EnterpriseSummary";
import VirtualSummary from "../bookmark_visuals/VirtualSummary";
import StorageAllUnits from "../bookmark_visuals/StorageAllUnits";
import StorageEnvironment from "../bookmark_visuals/StorageEnvironment";
import EnterpriseTrends from "../bookmark_visuals/EnterpriseTrends";
import HealthReport from "../bookmark_visuals/HealthReport";
import EnterprisePerformance from "../bookmark_visuals/EnterprisePerformance";
import StorageByGroup from "../bookmark_visuals/StorageByGroup";
import FreeSpaceByDevice from "../bookmark_visuals/FreeSpaceByDevice";
import DiskTypeByDevice from "../bookmark_visuals/DiskTypeByDevice";
import DeviceTrends from "../bookmark_visuals/DeviceTrends";
import EnterpriseSnap from "../bookmark_visuals/EnterpriseSnap";
import EntHostSummary from "../bookmark_visuals/HostSummary";
import SwitchesSummary from "../bookmark_visuals/SwitchesSummary";
import BUExecutiveOverview from "../bookmark_visuals/BUExecutiveOverview";
import FileTrends from "../bookmark_visuals/FileTrends";
import BackupRuntime from "../bookmark_visuals/BackupRuntime";
import EndOfSupportCalendar from "../bookmark_visuals/EndOfSupportCalendar";
import CloudBudgetTrend from "../bookmark_visuals/CloudBudgetTrend";
import CloudGrowthOverview from "../bookmark_visuals/CloudGrowthOverview";
import CloudResourceGroupForecast from "../bookmark_visuals/CloudResourceGroupForecast";
import CloudResourceModeling from "../bookmark_visuals/CloudResourceModeling";
import VirtualEnvironment from "../bookmark_visuals/VirtualEnvironment";
import BackupStatus from "../bookmark_visuals/BackupStatus";
import BackupGapAnalysis from "../bookmark_visuals/BackupGapAnalysis";
import BackupTrend from "../bookmark_visuals/BackupTrend";
import FileSavings from "../bookmark_visuals/FileSavings";
import FileByLastUsed from "../bookmark_visuals/FilesByLastUsed";
import BUChargebackReport from "../bookmark_visuals/BUChargeback";
import HostTrends from "../bookmark_visuals/HostTrends";
import HostLunPerformanceDetail from "../bookmark_visuals/HostLunPerformanceDetail";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";
import VirtualAllUnits2 from "../bookmark_visuals/VirtualAllUnits2";

const linkStyle = { textDecoration: "none", color: "inherit" };
const cardStyle = { maxHeight: 500, overflowY: "hidden", overflowX: "hidden" };

function NavCardFavs({
                       title,
                       path,
                       stateRollupKey,
                       context,
                       params,
                       _id,
                       periodId,
                       clientId,
                       contextIcon,
                       layout = []
                     }) {
  const { theme, isTabletScreen, user, getAccessTokenSilently, setUser, setAllFavoritesItems } = useVsiAppContext();

  const [isHoveringX, setIsHoveringX] = useState(false);
  const [isToolTipOpen] = useState(false);


  const linkRoute = () => {
    let qsp = "";
    params?.forEach?.(({ param, value }) => qsp = constructQsp(qsp, param, value));
    return `${path}${qsp}`;
  };

  const onDeleteClick = async e => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const token = await getAccessTokenSilently();
      const deleteFav = await DeleteAuth(`/api/v6/admin/favorite/${user._id}/${_id}`, token);
      if (deleteFav) {
        setUser(user => {
          return {
            ...user,
            favorites: user.favorites.filter(route => route._id !== _id)
          };
        });
        setAllFavoritesItems(allFavs => allFavs.filter(route => route._id !== _id));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const cardComponent = useMemo(() => {
    switch (title) {
      // Storage
      case "Enterprise Storage Summary":
        return <EnterpriseSummary clientId={clientId} periodId={periodId} />;
      // All Units (both)
      case "All Units":
        if (context === "Storage") {
          return <StorageAllUnits clientId={clientId} periodId={periodId} params={params} />;
        } else {
          return <VirtualAllUnits2 clientId={clientId} periodId={periodId} params={params} />;
        }
      case "Storage Environment":
        return <StorageEnvironment clientId={clientId} periodId={periodId} params={params?.[0] ?? {}} />;
      case "Trends":
        return <EnterpriseTrends clientId={clientId} periodId={periodId} params={params} />;
      case "Health Report":
        return <HealthReport clientId={clientId} periodId={periodId} />;
      case "Health Alerts":
        return <HealthReport clientId={clientId} periodId={periodId} />;
      case "Monthly Performance":
      case "Delta Report":
        return <EnterprisePerformance clientId={clientId} periodId={periodId} params={params} />;
      case "Storage by Group":
        return <StorageByGroup clientId={clientId} periodId={periodId} params={params?.[0] ?? {}} />;
      case "Free Space by Group":
        return <FreeSpaceByDevice clientId={clientId} periodId={periodId} params={params} />;
      case "Disk Type by Device":
        return <DiskTypeByDevice clientId={clientId} periodId={periodId} params={params} />;
      case "Device Trends":
        return <DeviceTrends clientId={clientId} periodId={periodId} params={params} />;
      case "Snap / Replication Details":
        return <EnterpriseSnap clientId={clientId} periodId={periodId} params={params} />;
      // Virtual
      case "Virtual Summary":
        return <VirtualSummary clientId={clientId} periodId={periodId} />;
      case "Virtual Environment":
        return <VirtualEnvironment clientId={clientId} periodId={periodId} />;
      // Hosts
      case "Enterprise Host Summary":
        return <EntHostSummary clientId={clientId} periodId={periodId} />;
      case "Host Trends":
        return <HostTrends clientId={clientId} periodId={periodId} params={params} />;
      case "Host LUN Performance":
        return <HostLunPerformanceDetail clientId={clientId} periodId={periodId} params={params} />;
      // Switches
      case "Enterprise Switch Summary":
        return <SwitchesSummary clientId={clientId} periodId={periodId} />;
      // Business Units
      case "Executive Overview":
        return <BUExecutiveOverview clientId={clientId} periodId={periodId} />;
      case "End of Support Calendar":
        return <EndOfSupportCalendar clientId={clientId} periodId={periodId} params={params} />;
      case "Chargeback Report":
        return <BUChargebackReport clientId={clientId} periodId={periodId} params={params} />;
      // Files
      case "File Trends":
        return <FileTrends clientId={clientId} periodId={periodId} />;
      case "File Savings":
        return <FileSavings clientId={clientId} periodId={periodId} />;
      case "File by Last Used Date":
        return <FileByLastUsed clientId={clientId} periodId={periodId} />;
      // Backup
      case "Backup Runtime":
        return <BackupRuntime clientId={clientId} periodId={periodId} />;
      case "Backup Status":
        return <BackupStatus clientId={clientId} periodId={periodId} />;
      case "Backup Gap Analysis":
        return <BackupGapAnalysis clientId={clientId} periodId={periodId} />;
      case "Backup Summary Trend":
        return <BackupTrend clientId={clientId} periodId={periodId} />;
      // Cloud
      case "Budget/Spending Trend":
        return <CloudBudgetTrend clientId={clientId} periodId={periodId} />;
      case "Growth Overview":
        return <CloudGrowthOverview clientId={clientId} periodId={periodId} />;
      case "Resource Group Forecast":
        return <CloudResourceGroupForecast clientId={clientId} periodId={periodId} params={params} />;
      case "Resource Group Modeling":
        return <CloudResourceModeling clientId={clientId} periodId={periodId} params={params} />;

      default:

        return (
          <>
            {params ? (
              params.map(({ param, value }, index) => (
                <VsiSpanText key={`descriptor-bookmark-${param}-${index} pb-0 mb-0`}>
                  <VsiSmallText className={"pe-2"}>{snakeCaseToTitleCase(param)}:</VsiSmallText>
                  <VsiSmallText className={"pe-2"}>{value}</VsiSmallText>
                </VsiSpanText>
              ))
            ) : null}
            <div className={"h-100"}>
              {layout.map((config = {}, index) => {
                if (index !== 0 && isTabletScreen) return null;
                return (
                  <NavCardBody
                    {...config}
                    params={params}
                    isBookmark={true}
                    stateRollupKey={stateRollupKey}
                    key={`nav-card-body-${config.title
                      .toLowerCase()
                      .split(" ")
                      .join("-")}-${index}`}
                  />
                );
              })}
            </div>
          </>
        );

    }
  }, [title, periodId, clientId, params, context, layout, isTabletScreen, stateRollupKey]);

  const uriRoute = linkRoute();

  return (
    <Link to={uriRoute} style={linkStyle}>
      <Card className={`mt-3 p-2 nav-card-${theme}`}
            style={cardStyle}>
        <div className={"d-flex flex-row justify-content-between align-items-center"}>
          <div className={"d-flex flex-row justify-content-center align-items-baseline"}>
            <i className={`fa-regular ${contextIcon} pe-2`}
               style={{ color: themeStyles[theme].reportBtnIconColor }} />
            <VsiH6 className={"fw-bold"}>
              {title}
              {params ? (
                params.map(({ param, value }, index) => (
                    <VsiSpanText key={`${index}-${value}`}> - {snakeCaseToTitleCase(value)} </VsiSpanText>
                  )
                )) : null}
            </VsiH6>
            <VsiSmallText className="fst-italic 50 ms-2">
              Click to see full report
            </VsiSmallText>
          </div>
          <div className={"d-flex flex-row justify-content-end report-util-btn"}>
            <i className={`fa-${isHoveringX ? "regular" : "solid"} fa-bookmark p-1 report-util-icon`}
               id={`bookmark-x-${_id}`}
               style={{ color: themeStyles[theme].vsiPupil }}
               onMouseEnter={() => setIsHoveringX(true)}
               onMouseLeave={() => setIsHoveringX(false)}
               onClick={onDeleteClick} />
            <Tooltip
              isOpen={isToolTipOpen}
              target={`bookmark-x-${_id}`}
              // toggle={toggle}
            >
              Remove bookmarked report
            </Tooltip>
          </div>
        </div>
        {cardComponent}
        <VsiSmallText className={"fst-italic"}>{context}</VsiSmallText>
      </Card>
    </Link>
  );
}

export default withErrorBoundary(NavCardFavs, GeneralErrorBoundary);
